import axios                 from 'axios';
import FileModel             from '@/models/services/file/StonecodeFileFileModel';
import { blobToBase64Async } from '@widesk-core/tools/blobTools';
import authStore             from '@widesk-core/stores/authStore';
import { API_ENV }           from '@/appConfig';
import uniqueId              from 'lodash/uniqueId';

type CacheType = 'b64' | 'responseUrl';

const cacheSrc: Record<CacheType, Record<string, string>> = { b64: {}, responseUrl: {} };
const cacheSrcByUrn: Record<CacheType, Record<Urn, string>> = { b64: {}, responseUrl: {} };
const cacheSrcPromises: Record<CacheType, Record<string, Promise<string>>> = { b64: {}, responseUrl: {} };

const fetchSrcForFile = async (
	file: FileModel,
	options?: { mimeType?: string; type?: CacheType; force?: boolean; },
): Promise<string> => {
	const type = options?.type ?? 'b64';

	const path = file.downloadPath + (options?.force ? `?rand=${uniqueId()}` : '');

	if (!path) return '';

	if (!options?.force) {
		// Cache par URL
		if (cacheSrc[type][path]) return cacheSrc[type][path] || '';

		// Cache par URN
		if (cacheSrcByUrn[type][file.urn]) return cacheSrcByUrn[type][file.urn] || '';

		// Une requête est déjà en cours
		if (cacheSrcPromises[type][path]) return cacheSrcPromises[type][path] || '';
	}

	cacheSrcPromises[type][path] = (async () => {
		try {
			const response = await axios.get(path, {
				headers: {
					'X-Platform-Authorization': `Bearer ${authStore.token}`,
				},
				responseType: type === 'b64' ? 'blob' : undefined,
			});

			const src =
				type === 'responseUrl'
					? response.request.responseURL
					: await blobToBase64Async(response.data, options?.mimeType) as string;

			cacheSrc[type][path] = src;
			cacheSrcByUrn[type][file.urn] = src;

			return src;
		} catch (e) {
			if (axios.isAxiosError(e)) {
				console.log({
					url: path,
					code: e.code,
					message: e.message,
					status: e.response?.status,
					response: e.response,
					request: e.request,
				});
			}

			throw e;
		} finally {
			delete cacheSrcPromises[type][path];
		}
	})();

	return cacheSrcPromises[type][path] || '';
};

export const amazonPath = API_ENV === 'dev' ?
	`https://wbcdevtests.s3.eu-west-1.amazonaws.com/dev-tokamap-file/` :
	`https://tokamap-prod.s3.eu-west-3.amazonaws.com/production-tokamap-file/`;

const getAmazonUrlFromFileId = (id?: id | null, path?: string) => {
	if (id) return `${amazonPath}map/${path}${id.padStart(12, '0')}`;
	return '';
};

const getCachedSrc = (options: {
	type?: CacheType;
	for: FileModel | Urn;
}) => {
	const type = options.type ?? 'b64';

	if (options.for instanceof FileModel) {
		const path = options.for.downloadPath;

		if (path && cacheSrc[type][path]) {
			return cacheSrc[type][path];
		}
	} else {
		const urn = options.for;

		if (cacheSrcByUrn[type][urn]) {
			return cacheSrcByUrn[type][urn];
		}
	}

	return '';
};

export default {
	fetchSrcForFile,
	getCachedSrc,
	getAmazonUrlFromFileId,
};